/** @jsxImportSource @emotion/react */
/**
 * CN-0015: ManagementPanel — Contextual info + actions for selected bed/planting
 *
 * Shown in management mode when a bed is selected on the plan grid.
 * Displays:
 * - Bed info (name, area, cells)
 * - Planting info (crop, status) if bed has a planting
 * - Quick action buttons contextually:
 *   - Empty bed: "Посадить" + bed actions
 *   - Bed with planting: bed actions + planting actions
 * - Navigation links to detail pages
 */
import React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Divider, Space, Switch, Tag, Typography } from 'antd';
import {
  EyeOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  ExperimentOutlined,
  ToolOutlined,
  AimOutlined,
} from '@ant-design/icons';
import type { BedPlantingInfo } from '../../types/planManagement';
import { buildActionContext } from '../../types/planManagement';
import { PLANTING_STATUS_META } from '../../features/plantings/constants';
import type { PlantingLifecycleStatus } from '../../models/Planting';
import { LIFECYCLE_PHASE_LABELS } from '../../pages/plantings/planting-details/constants';

const { Text } = Typography;

const styles = {
  panel: css({
    width: '100%',
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    overflow: 'hidden',
  }),
  header: css({
    padding: '12px 16px',
    background: '#fafafa',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  body: css({
    padding: '12px 16px',
  }),
  section: css({
    marginBottom: 12,
  }),
  sectionLabel: css({
    fontSize: 12,
    color: '#8c8c8c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: 6,
    display: 'block',
  }),
  infoRow: css({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 4,
    fontSize: 13,
  }),
  plantingCard: css({
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    borderBottom: '1px solid #f0f0f0',
  }),
  plantingIndex: css({
    color: '#8c8c8c',
    textAlign: 'right' as const,
  }),
  plantingLink: css({
    color: '#1677ff',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  }),
  lifecycleHint: css({
    marginTop: 2,
    color: '#8c8c8c',
    fontSize: 12,
  }),
  emptyBed: css({
    padding: '10px 12px',
    background: '#fff7e6',
    border: '1px solid #ffd591',
    borderRadius: 6,
    textAlign: 'center' as const,
    marginBottom: 8,
  }),
  actionsGrid: css({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  }),
  actionBtn: css({
    width: '100%',
    textAlign: 'left' as const,
    height: 'auto',
    padding: '8px 12px',
    '& .ant-btn-icon': {
      marginRight: 8,
    },
  }),
  multiSelectInfo: css({
    padding: '12px 16px',
    background: '#e6f7ff',
    border: '1px solid #91d5ff',
    borderRadius: 6,
    marginBottom: 12,
  }),
};

const labelPhase = (phase?: string | null): string => (
  phase ? (LIFECYCLE_PHASE_LABELS[phase] || phase) : ''
);

const formatLifecycleProgress = (value?: number | null): string | null => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const pct = num <= 1 ? num * 100 : num;
  return `${Math.max(0, Math.min(100, Math.round(pct)))}%`;
};

interface ManagementPanelProps {
  /** Selected bed info enriched with planting data */
  bedInfo: BedPlantingInfo | null;
  /** Multiple selected bed IDs with their info */
  multiSelectedBeds: Map<string, BedPlantingInfo>;
  /** Auto-watering state for selected bed */
  bedAutoWatering?: boolean;
  /** Supplemental lighting state for selected bed */
  bedLighting?: boolean;
  /** Per-target action gates, used by scenarios. */
  canRunBedAction?: (bedId: string) => boolean;
  canRunPlantingAction?: (plantingId: string) => boolean;
  allowAddPlanting?: boolean;
  /** Callbacks */
  onViewBed: (bedId: string) => void;
  onViewPlanting: (plantingId: string) => void;
  onAddPlanting: (bedId: string) => void;
  onBedAction: (bedIds: string[]) => void;
  onPlantingAction: (targets: Array<{ plantingId?: string; bedId?: string }>) => void;
  onClearSelection: () => void;
  onPlantingAutoHarvestToggle?: (plantingId: string, value: boolean) => void;
  onBedAutoWateringToggle?: (value: boolean) => void;
  onBedLightingToggle?: (value: boolean) => void;
}

const ManagementPanel: React.FC<ManagementPanelProps> = ({
  bedInfo,
  multiSelectedBeds,
  bedAutoWatering,
  bedLighting,
  canRunBedAction = () => true,
  canRunPlantingAction = () => true,
  allowAddPlanting = true,
  onViewBed,
  onViewPlanting,
  onAddPlanting,
  onBedAction,
  onPlantingAction,
  onClearSelection,
  onPlantingAutoHarvestToggle,
  onBedAutoWateringToggle,
  onBedLightingToggle,
}) => {
  const { t } = useTranslation('planning');
  const [selectedPlantingIds, setSelectedPlantingIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (!bedInfo) {
      setSelectedPlantingIds(new Set());
      return;
    }
    const activeIds = bedInfo.plantings
      .filter((p) => p.lifecycleStatus === 'active')
      .map((p) => p.id);
    setSelectedPlantingIds(new Set(activeIds));
  }, [bedInfo]);

  const isMultiSelect = multiSelectedBeds.size > 1;
  const hasSelection = bedInfo !== null || multiSelectedBeds.size > 0;

  if (!hasSelection) {
    return (
      <div css={styles.panel}>
        <div css={styles.body} style={{ textAlign: 'center', padding: '24px 16px' }}>
          <AimOutlined style={{ fontSize: 32, color: '#bfbfbf', marginBottom: 8 }} />
          <br />
          <Text type="secondary">
            {t('management.emptyHint')}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('management.multiSelectHint')}
          </Text>
        </div>
      </div>
    );
  }

  // --- Multi-select mode ---
  if (isMultiSelect) {
    const allBeds = Array.from(multiSelectedBeds.values());
    const bedsWithPlantings = allBeds.filter((b) => b.plantings.some((p) => p.lifecycleStatus === 'active'));
    const emptyBeds = allBeds.filter((b) => !b.plantings.some((p) => p.lifecycleStatus === 'active'));
    const allBedIds = allBeds.map((b) => b.bedId);
    const actionableBedIds = allBedIds.filter(canRunBedAction);

    // Collect planting targets for batch actions
    const plantingTargets = bedsWithPlantings.flatMap((b) =>
      b.plantings
        .filter((p) => p.lifecycleStatus === 'active' && canRunPlantingAction(p.id))
        .map((p) => ({ plantingId: p.id, bedId: b.bedId }))
    );

    return (
      <div css={styles.panel}>
        <div css={styles.header}>
          <Text strong>{t('management.selectedBeds', { count: multiSelectedBeds.size })}</Text>
          <Button type="link" size="small" onClick={onClearSelection}>
            {t('common:actions.reset')}
          </Button>
        </div>
        <div css={styles.body}>
          <div css={styles.multiSelectInfo}>
            <Space direction="vertical" size={4}>
              {bedsWithPlantings.length > 0 && (
                <Text>
                  <ExperimentOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                  {t('management.withPlantings', { count: bedsWithPlantings.length })}
                </Text>
              )}
              {emptyBeds.length > 0 && (
                <Text>
                  <Tag color="orange">{t('management.emptyBeds', { count: emptyBeds.length })}</Tag>
                </Text>
              )}
            </Space>
          </div>

          <div css={styles.section}>
            <span css={styles.sectionLabel}>{t('management.groupActions')}</span>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {actionableBedIds.length > 0 && (
                <Button
                  type="primary"
                  icon={<ToolOutlined />}
                  block
                  onClick={() => onBedAction(actionableBedIds)}
                >
                  {t('management.bedsAction', { count: actionableBedIds.length })}
                </Button>
              )}
              {plantingTargets.length > 0 && (
                <Button
                  icon={<ThunderboltOutlined />}
                  block
                  style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                  onClick={() => onPlantingAction(plantingTargets)}
                >
                  {t('management.plantingsAction', { count: plantingTargets.length })}
                </Button>
              )}
            </Space>
          </div>
        </div>
      </div>
    );
  }

  // --- Single bed selection ---
  if (!bedInfo) return null;

  const ctx = buildActionContext(bedInfo);
  const activePlantings = bedInfo.plantings.filter((p) => p.lifecycleStatus === 'active');
  const selectedPlantings = activePlantings.filter((p) => selectedPlantingIds.has(p.id));
  const canActOnBed = canRunBedAction(bedInfo.bedId);
  const actionablePlantings = selectedPlantings.filter((p) => canRunPlantingAction(p.id));
  const canActOnPlantings = activePlantings.some((p) => canRunPlantingAction(p.id));

  return (
    <div css={styles.panel}>
      <div css={styles.header}>
        <Text strong>{bedInfo.bedName || t('bedName.withId', { id: bedInfo.bedId.slice(0, 8) })}</Text>
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onViewBed(bedInfo.bedId)}
        >
          {t('common:actions.open')}
        </Button>
      </div>
      <div css={styles.body}>
        {/* Bed info */}
        {bedInfo.area != null && (
          <div css={styles.infoRow}>
            <Text type="secondary">{t('management.area')}</Text>
            <Text>{bedInfo.area} {t('common:units.m2')}</Text>
          </div>
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* Planting info or empty state */}
        {ctx.isEmpty && ctx.hasBlockingPlanting && ctx.blockingPlanting ? (
          <div css={styles.emptyBed} style={{ background: '#fff1f0', borderColor: '#ffa39e' }}>
            <Text type="danger">
              {t('management.bedOccupied')}{' '}
              <Text css={styles.plantingLink} onClick={() => onViewPlanting(ctx.blockingPlanting!.id)}>
                {ctx.blockingPlanting.cropName}
              </Text>
            </Text>
            <br />
            <Tag color={PLANTING_STATUS_META[ctx.blockingPlanting.lifecycleStatus as PlantingLifecycleStatus]?.color || 'default'} style={{ marginTop: 4 }}>
              {PLANTING_STATUS_META[ctx.blockingPlanting.lifecycleStatus as PlantingLifecycleStatus]?.label || ctx.blockingPlanting.lifecycleStatus}
            </Tag>
            <br />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {t('management.bedOccupiedHint')}
            </Text>
          </div>
        ) : ctx.isEmpty ? (
          <div css={styles.emptyBed}>
            <Text type="warning">{t('management.bedEmpty')}</Text>
            <br />
            {allowAddPlanting && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                style={{ marginTop: 8 }}
                onClick={() => onAddPlanting(bedInfo.bedId)}
              >
                {t('management.plant')}
              </Button>
            )}
          </div>
        ) : (
          <div css={styles.section}>
            <span css={styles.sectionLabel}>{t('management.plantings')}</span>
            {bedInfo.plantings
              .filter((p) => p.lifecycleStatus === 'active')
              .map((planting, idx) => (
                <div key={planting.id} css={styles.plantingCard}>
                  {canRunPlantingAction(planting.id) ? (
                    <Checkbox
                      checked={selectedPlantingIds.has(planting.id)}
                      onChange={(e) => {
                        const next = new Set(selectedPlantingIds);
                        if (e.target.checked) next.add(planting.id);
                        else next.delete(planting.id);
                        setSelectedPlantingIds(next);
                      }}
                    />
                  ) : <span />}
                  <div>
                    <Text css={styles.plantingLink} onClick={() => onViewPlanting(planting.id)}>
                      {idx + 1}. {planting.cropName}
                    </Text>
                    {(planting.lifecyclePhase || planting.lifecycleGateProgressPct != null || planting.lifecycleNextPhase) && (
                      <div css={styles.lifecycleHint}>
                        {labelPhase(planting.lifecyclePhase) || t('management.lifecycle')}
                        {planting.lifecycleNextPhase ? ` -> ${labelPhase(planting.lifecycleNextPhase)}` : ''}
                        {formatLifecycleProgress(planting.lifecycleGateProgressPct)
                          ? ` · ${formatLifecycleProgress(planting.lifecycleGateProgressPct)}`
                          : ''}
                      </div>
                    )}
                  </div>
                  {planting.quantity && planting.quantity > 1 ? (
                    <Tag color="blue">{t('management.quantity', { count: planting.quantity })}</Tag>
                  ) : (
                    <span />
                  )}
                </div>
              ))}
          </div>
        )}

        {(canActOnBed || canActOnPlantings) && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            {/* Action buttons */}
            <div css={styles.section}>
              <span css={styles.sectionLabel}>{t('common:labels.actions')}</span>
          {/* Bed-level and planting-level actions stay separate: care actions like watering
              target the bed, so the bed action must remain reachable even when a planting exists. */}
          <div css={styles.actionsGrid}>
            {canActOnBed && (
              <Button
                css={styles.actionBtn}
                icon={<ToolOutlined />}
                onClick={() => onBedAction([bedInfo.bedId])}
              >
                {t('management.bedAction')}
              </Button>
            )}
            {canActOnPlantings && (
              <Button
                css={styles.actionBtn}
                icon={<ThunderboltOutlined />}
                style={{ borderColor: '#52c41a', color: '#52c41a' }}
                disabled={actionablePlantings.length === 0}
                onClick={() =>
                  onPlantingAction(
                    actionablePlantings.map((p) => ({ plantingId: p.id, bedId: bedInfo.bedId }))
                  )
                }
              >
                {t('management.plantingAction', { count: actionablePlantings.length })}
              </Button>
            )}
          </div>
            </div>
          </>
        )}

        {/* Automation toggles */}
        {(onPlantingAutoHarvestToggle || onBedAutoWateringToggle || onBedLightingToggle) && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <div css={styles.section}>
              <span css={styles.sectionLabel}>{t('management.automation')}</span>
              {onBedAutoWateringToggle && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text>{t('management.autoWatering')}</Text>
                  <Switch
                    size="small"
                    checked={bedAutoWatering ?? false}
                    onChange={onBedAutoWateringToggle}
                  />
                </div>
              )}
              {onBedLightingToggle && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text>{t('management.lighting')}</Text>
                  <Switch
                    size="small"
                    checked={bedLighting ?? false}
                    onChange={onBedLightingToggle}
                  />
                </div>
              )}
              {onPlantingAutoHarvestToggle && activePlantings.length > 0 && (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {activePlantings.map((planting) => (
                    <div
                      key={planting.id}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                    >
                      <Text ellipsis style={{ maxWidth: 210 }}>
                        {activePlantings.length === 1 ? t('management.autoHarvest') : planting.cropName}
                      </Text>
                      <Switch
                        size="small"
                        checked={planting.autoHarvest === true}
                        onChange={(checked) => onPlantingAutoHarvestToggle(planting.id, checked)}
                      />
                    </div>
                  ))}
                </Space>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManagementPanel;
